import React, { useEffect, useState, useRef } from "react";
import { clearSession } from "../../shared/_helpers/session";
import DashboardHeader from "./DashboardHeader";
import DashboardUserHeader from "./DashboardUserHeader";
import { getOnboardingDetails, getOldOnboardingData, checkBlockedStatus, getDashboardInsights, getPlaidTransactions, getFinancialGoals } from "../../shared/_services/common.services";
import { getMyThread, sendThreadMessage, uploadThreadAttachments } from "../../shared/_services/thread.services";
import { ACCEPT_ATTR, validateFiles } from "../../shared/_helpers/attachmentPolicy";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../../shared/_helpers/apiErrors";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { CHOOSE_PLAN_PATH, hasChatAccess } from "../../shared/_helpers/chatGate";
import { questionsEditPath } from "../intake/intakeNav";
import BookAppointmentModal from "../AppointmentBooking";
import AmountPlaceholder from "../../assets/images/AmountPlaceholder.svg";
import RecentActivityPlaceholder from "../../assets/images/RecentActivityPlaceholder.svg";
import FinancialGoalsPlaceholder from "../../assets/images/FinancialGoalsPlaceholder.svg";

import PrioritiesPlaceholder from "../../assets/images/PrioritiesPlaceholder.svg";
import AssetsPlaceholder from "../../assets/images/AssetsPlaceholder.svg";
import DebtPlaceholder from "../../assets/images/DebtPlaceholder.svg";
import ShortGoalsPlaceholder from "../../assets/images/ShortGoalsPlaceholder.svg";
import LongGoalsPlaceholder from "../../assets/images/LongGoalsPlaceholder.svg";
import DashboardCharts from "../DashboardChart";
import Loader from "../Loader";
import { useSnackbar } from "notistack";
import { StaffAnswers } from "../intake/QuestionsPage";
import moment from "moment";
import 'chart.js/auto';
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dummy_assets_screenshot from '../../assets/images/dummy_assets_screenshot.jpg';
import dummy_debt_screenshot from '../../assets/images/dummy_debt_screenshot.jpg';
import NonSubLockBox from "./NonSubLockBox";
import InsightsPanel, { normalizeInsights } from "./InsightsPanel";
import othersImg from "../../assets/images/Other2.0.svg"
import dbOthers from "../../assets/images/dbOther.svg"
import StarsIcon from "../../styles/StarsIcon";
import { formatUsd } from "../../shared/_helpers/formatUsd";
import { readToken } from "../../styles/readToken";
import {
    Button, Dialog, DialogActions, DialogClose, DialogContent, DialogDescription, DialogTitle,
    Page, Panel, Pass, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, Tooltip, TooltipProvider,
} from "../ui";

Chart.register(ArcElement, ChartDataLabels);

// Chart ladders (DESIGN.md "Chart ladders"): teal for assets, violet for debts, read
// from the element so .stars-dark flips them. Charts never hold a hex.
const ASSET_STEPS = ["chart-a1", "chart-a2", "chart-a3", "chart-a4"];
const DEBT_STEPS = ["chart-d1", "chart-d2", "chart-d3"];
// Steps go up the ladder and back down, so neighbouring slices never share a colour; the
// last slice also differs from the first, which it touches in a ring.
const stepIndex = (i, len) => {
    if (len < 2) return 0;
    const m = i % (2 * len - 2);
    return m < len ? m : 2 * len - 2 - m;
};
const ladder = (steps, n, node) => {
    const idx = Array.from({ length: n }, (_, i) => stepIndex(i, steps.length));
    if (n > 2 && steps.length > 2 && idx[n - 1] === idx[0]) {
        idx[n - 1] = idx[0] + 1 === idx[n - 2] ? idx[0] + 2 : idx[0] + 1;
    }
    return idx.map((k) => readToken(steps[k], node));
};

// Under 820px the wallet passes go compact (the Stacked Wallet rule). The kit has no
// media-query hook and Pass's compact is a prop, so this reads the same breakpoint.
function useIsPhone() {
    const query = "(max-width: 820px)";
    const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
    const [phone, setPhone] = useState(read);
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;
        const mq = window.matchMedia(query);
        const onChange = () => setPhone(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return phone;
}

const LINK_CLASS = "tw-block tw-text-text tw-no-underline hover:tw-text-text";
const ROW_CLASS = "tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-3 last:tw-border-b-0";
const ROW_START_CLASS = "tw-flex tw-items-center tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-divider tw-py-3 last:tw-border-b-0";
const PLACEHOLDER_CLASS = "tw-grid tw-place-items-center tw-py-6";

function hasChartBreakdownData(chartData) {
    return Boolean(chartData?.datasets) &&
        Boolean(chartData.datasets[0]?.data) &&
        chartData.datasets[0].data.length > 0;
}

function getNetWorthDisplayValue(hasNetWorthData, onboardingDetails, calculateTotalInvestment, calculateTotalDebt) {
    if (!hasNetWorthData) return formatUsd(0);
    return formatUsd(
        calculateTotalInvestment(onboardingDetails["Investments"]) -
        calculateTotalDebt(onboardingDetails["Debts"])
    );
}

// R213: the change chip compares the EARLIEST summaryUpdateHistory entry's
// total with the NEWEST, and exists only when at least two entries do. A zero
// earliest total (or no change) hides it; never a division by zero, never
// "Infinity%" or "NaN%". The row's own top-level total is the latest, not a
// baseline (SP4 B2).
function historyChange(row) {
    const history = row?.summaryUpdateHistory;
    if (!Array.isArray(history) || history.length < 2) return null;
    const sorted = [...history].sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
    const earliest = Number(sorted[0]?.totalAssets);
    const newest = Number(sorted[sorted.length - 1]?.totalAssets);
    if (!Number.isFinite(earliest) || !Number.isFinite(newest) || earliest === 0) return null;
    const change = ((newest - earliest) / earliest) * 100;
    return Number.isFinite(change) && change !== 0 ? change : null;
}

function hasNamedPriorities(priorities) {
    return Array.isArray(priorities) && priorities.some((priority) => priority?.name);
}

function hasPlanEntries(plans) {
    return Array.isArray(plans) && plans.length > 0;
}

function hasNetWorthDataFrom(onboardingDetails) {
    return (onboardingDetails?.["Investments"]?.length || 0) > 0 ||
        (onboardingDetails?.["Debts"]?.length || 0) > 0;
}

function handleDashboardEntryAction(locationState, openAskQuestionPopup, setBookAppointmentPopup) {
    const actionType = locationState?.actionType;
    if (actionType === "MESSAGE") {
        openAskQuestionPopup();
        return;
    }
    if (actionType === "VIDEO_CALL") {
        setBookAppointmentPopup(true);
    }
}

function renderDashboardHeader(isClientDashboardUser, clientId) {
    return isClientDashboardUser
        ? <DashboardHeader />
        : <DashboardUserHeader clientId={clientId} />;
}

function renderWhen(condition, content) {
    return condition ? content : null;
}

function renderLoadingState(showLoader, content) {
    return showLoader ? <Loader /> : content;
}

function getAttachedFilesText(attachedFilesLength) {
    return `${attachedFilesLength} file${attachedFilesLength > 1 ? "s" : ""} attached`;
}

function renderAskAdvisorSendButtonContent(isUploading) {
    if (isUploading) {
        return (
            <span
                data-slot="send-btn-spinner"
                className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-pill tw-border-2 tw-border-solid tw-border-current tw-border-t-transparent motion-reduce:tw-animate-none"
            ></span>
        );
    }
    return (
        <>
            Send<StarsIcon name="send" className="tw-h-4 tw-w-4 tw-shrink-0" />
        </>
    );
}

function handleSessionExpiredIfUnauthorized(data, navigate, enqueueSnackbar) {
    if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
    }
}

// A round glyph for a priority or plan row; the legacy art is a 44px disc.
function OptionImage({ src, alt }) {
    return <img className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-pill tw-bg-surface-sunken tw-object-contain tw-p-2" src={src} alt={alt} />;
}

function renderTopPrioritiesContent(onboardingDetails, othersData) {
    if (hasNamedPriorities(onboardingDetails?.["What do you want help with first?"])) {
        return (
            <div data-slot="selected-top-priorities">
                {onboardingDetails["What do you want help with first?"].map((priority, index) => {
                    let imgSrc;
                    try {
                        imgSrc = require(`../../assets/images/db${priority.name.replace(/\s+/g, "")}.svg`);
                    } catch (error) {
                        imgSrc = require('../../assets/images/default1.png');
                    }
                    return (
                        priority?.name && (
                            <div className={ROW_START_CLASS} key={priority?.name}>
                                <OptionImage src={imgSrc} alt={priority?.name} />
                                <div>
                                    <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-text">{priority?.name}</p>
                                    <div className="tw-text-xs tw-text-text-muted">Priority {index + 1}</div>
                                </div>
                            </div>
                        )
                    );
                })}
                {othersData?.["What do you want help with first?"] && (
                    <div className={ROW_START_CLASS} key="others">
                        <OptionImage src={dbOthers} alt="Others" />
                        <div>
                            <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-text">{othersData["What do you want help with first?"]}</p>
                            <div className="tw-text-xs tw-text-text-muted">Others</div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={PLACEHOLDER_CLASS}>
            <img src={PrioritiesPlaceholder} alt="No priorities selected" />
        </div>
    );
}

// One legend row of a breakdown: a swatch in the slice's colour (a value computed
// from data, so it stays in `style`), the name, the amount and its share.
function BreakdownRows({ chartData, total }) {
    return (
        <div data-slot="breakdown-list" className="tw-max-h-64 tw-overflow-y-auto">
            {chartData.labels.map((label, index) => {
                const amount = chartData.datasets[0].data[index];
                const percentage = total > 0 ? ((amount / total) * 100).toFixed(1) : 0;
                return (
                    <div key={label} data-slot="breakdown-item" className={ROW_CLASS}>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <span
                                className="tw-h-3 tw-w-3 tw-shrink-0 tw-rounded-sm"
                                style={{ backgroundColor: chartData.datasets[0].backgroundColor[index] }}
                            ></span>
                            <span className="tw-text-sm tw-text-text">{label}</span>
                        </div>
                        <div className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-text">
                            {formatUsd(amount)} ({percentage}%)
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function BreakdownHeader({ total, caption, chartData, pieChartOptions }) {
    return (
        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div>
                <p className="tw-m-0 tw-text-2xl tw-font-bold tw-tabular-nums tw-text-text">
                    {formatUsd(total)}
                </p>
                <small className="tw-text-sm tw-text-text-muted">{caption}</small>
            </div>
            <div className="tw-relative tw-h-40 tw-w-40 tw-shrink-0">
                <Pie data={chartData} options={pieChartOptions} />
            </div>
        </div>
    );
}

function renderAssetsBreakdownContent(subscribed, hasInvestmentBreakdownData, onboardingDetails, investmentChartData, pieChartOptions, calculateTotalInvestment) {
    if (subscribed && hasInvestmentBreakdownData) {
        const total = calculateTotalInvestment(onboardingDetails["Investments"] || []);
        return (
            <>
                <BreakdownHeader total={total} caption="Total Assets" chartData={investmentChartData} pieChartOptions={pieChartOptions} />
                <BreakdownRows chartData={investmentChartData} total={total} />
            </>
        );
    }

    if (subscribed && !hasInvestmentBreakdownData) {
        return (
            <div className={PLACEHOLDER_CLASS}>
                <img height={160} width={160} src={AssetsPlaceholder} alt="" />
            </div>
        );
    }

    return <img className="tw-h-full tw-w-full" src={dummy_assets_screenshot} alt="" />;
}

function renderDebtBreakdownContent(subscribed, hasDebtBreakdownData, onboardingDetails, debtChartData, pieChartOptions, calculateTotalDebt) {
    if (subscribed && hasDebtBreakdownData) {
        const total = calculateTotalDebt(onboardingDetails["Debts"] || []);
        return (
            <>
                <BreakdownHeader total={total} caption="Total Debt" chartData={debtChartData} pieChartOptions={pieChartOptions} />
                <BreakdownRows chartData={debtChartData} total={total} />
            </>
        );
    }

    if (subscribed && !hasDebtBreakdownData) {
        return (
            <div className={PLACEHOLDER_CLASS}>
                <img height={160} width={160} src={DebtPlaceholder} alt="" />
            </div>
        );
    }

    return <img className="tw-h-full tw-w-full" src={dummy_debt_screenshot} alt="" />;
}

function renderPlanOptionsContent(onboardingDetails, othersData, sectionName) {
    if (hasPlanEntries(onboardingDetails?.[sectionName])) {
        return (
            <>
                {onboardingDetails[sectionName].map((term) => {
                    let imgSrc;
                    try {
                        imgSrc = require(`../../assets/images/${term.name.replace(/\s+/g, "")}2.0.svg`);
                    } catch (error) {
                        imgSrc = require(`../../assets/images/default1.png`);
                    }
                    return (
                        <div className={ROW_START_CLASS} key={term?.name}>
                            <OptionImage src={imgSrc} alt={term.name} />
                            <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">{term.name}</p>
                        </div>
                    );
                })}
                {othersData?.[sectionName] && (
                    <div className={ROW_START_CLASS} key="others">
                        <OptionImage src={othersImg} alt="Others" />
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">{othersData[sectionName]}</p>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className={PLACEHOLDER_CLASS}>
            <img height={160} width={160} src={sectionName === "Short-term" ? ShortGoalsPlaceholder : LongGoalsPlaceholder} alt="" />
        </div>
    );
}

const AMOUNT_TONES = { credit: "tw-text-success", debit: "tw-text-danger", salary: "tw-text-text-accent" };
const amountTone = (type) => AMOUNT_TONES[type] || "tw-text-text";
const changeTone = (value) => (value > 0 ? "tw-text-success" : "tw-text-danger");

// A wallet figure with its change chip (R213: the chip exists only with a change).
function Change({ value }) {
    if (value === null) return null;
    return (
        <span className={`tw-text-sm tw-font-bold ${changeTone(value)}`}>
            {value.toFixed(2)}%
        </span>
    );
}

function Figure({ children }) {
    return <p className="tw-m-0 tw-text-3xl tw-font-bold tw-tabular-nums tw-text-text">{children}</p>;
}

// The placeholder tile shown to a subscribed client with nothing entered yet.
function AmountEmpty() {
    return (
        <div>
            <img className="tw-h-10 tw-w-10" src={AmountPlaceholder} alt="" />
        </div>
    );
}

function Dashboard() {
    const [onboardingDetails, setOnboardingDetails] = useState({});
    const params = useParams();
    const navigate = useNavigate();
    const [thankYouPopupShow, setThankYouPopupShow] = useState(false);
    const [appointmentDetails, setAppointmentDetails] = useState({});
    const [bookAppointmentPopup, setBookAppointmentPopup] = useState(false);
    const [popupShow, setPopupShow] = useState(false);
    const [currentChartType, setCurrentChartType] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [oldInvestmentData, setOldInvestmentData] = useState([]);
    const [oldDebtData, setOldDebtData] = useState([]);
    const [newInvestmentData, setNewInvestmentData] = useState([]);
    const [newDebtData, setNewDebtData] = useState([]);
    const [investmentChartData, setInvestmentChartData] = useState({
        labels: [], datasets: [{ data: [], backgroundColor: [], }],
    });
    const [debtChartData, setDebtChartData] = useState({
        labels: [], datasets: [{ data: [], backgroundColor: [], }],
    });
    const [oldInvestmentChartData, setOldInvestmentChartData] = useState({
        labels: [], datasets: [{ data: [] }],
    });
    const [oldDebtChartData, setOldDebtChartData] = useState({
        labels: [], datasets: [{ data: [] }],
    });
    const [threadId, setThreadId] = useState(null);
    const [messageInput, setMessageInput] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const rootRef = useRef(null);
    const isPhone = useIsPhone();
    const [askQuestionPopupShow, setAskQuestionPopupShow] = useState(false);
    const askQuestionPopupClose = () => {
        setAskQuestionPopupShow(false);
        setAttachedFiles([]);
        setIsUploading(false);
    };
    const openAskQuestionPopup = () => {
        setAskQuestionPopupShow(true);
    };
    // INT-015: financial insights only; the market array is never read.
    const [financialInsights, setFinancialInsights] = useState([]);
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserType = currentUser?.user?.userType;
    const isClientDashboardUser = currentUserType === "client";
    const shouldShowQuestionnaireDetails = ["advisor", "super-admin", "admin"].includes(currentUserType);
    // R292: "paid" is the server's entitlement everywhere, so a lapsed client
    // gets the lock boxes too. A client without chat access is sent to the plan
    // page by Message, Book a Call and the entry state (R263), and the thread
    // is not requested (it would only 402).
    const { user: authUser, status: authStatus } = useAuth();
    const subscribed = hasChatAccess(authUser);
    const chatClosed = isClientDashboardUser && !subscribed;
    const [plaidActivities, setPlaidActivities] = useState([]);
    const [financialGoals, setFinancialGoals] = useState([]);
    const [othersData, setOthersData] = useState({});

    const location = useLocation();

    useEffect(() => {
        if (authStatus === "loading") return;
        if (chatClosed) {
            if (location.state?.actionType === "MESSAGE" || location.state?.actionType === "VIDEO_CALL") {
                navigate(CHOOSE_PLAN_PATH, { replace: true });
            }
            return;
        }
        handleDashboardEntryAction(location.state, openAskQuestionPopup, setBookAppointmentPopup);
        // getMyThread() is client-only server-side (403 for advisor/admin) —
        // only the client sees the Ask a Question popup this loads for, so
        // this is scoped the same way to avoid a spurious error toast on an
        // advisor/admin viewing this same component for a client.
        if (isClientDashboardUser) {
            getMyThread()
                .then((r) => setThreadId(r.data.thread._id))
                .catch(() => enqueueSnackbar("Couldn't open your thread", { variant: 'error' }));
        }
    }, [location.state, authStatus]); //eslint-disable-line


    const getData = async () => {
        setShowLoader(true);

        const currentUser = JSON.parse(localStorage.getItem('currentUser')) || {};
        const userId = currentUser.user?._id;

        let data = await getOnboardingDetails(null, userId, 'dashboard');
        let oldData = await getOldOnboardingData(userId);


        if (data.status !== 200) {
            handleSessionExpiredIfUnauthorized(data, navigate, enqueueSnackbar);
            if (isForbiddenClientAccess(data)) {
                enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
                navigate(CLIENT_ACCESS_DENIED_REDIRECT);
            }
            setShowLoader(false);
            return;
        }

        const investmentData = data?.data?.data?.find(item => item?.questionName === "Investments")?.selectedOptions || [];
        setNewInvestmentData(investmentData);

        const investmentQuestion = oldData?.data?.find(question => question.questionName === "My Investments");
        const oldInvestmentInfo = investmentQuestion?.allotandDebtOptions || [];
        const oldTotalAssets = investmentQuestion?.totalAssets || 0;
        setOldInvestmentData({ oldInvestmentInfo, oldTotalAssets, change: historyChange(investmentQuestion) });

        const debtData = data?.data?.data?.find(item => item?.questionName === "Debts")?.selectedOptions || [];
        setNewDebtData(debtData);

        const debtQuestion = oldData?.data?.find(question => question.questionName === "My Debts");
        const oldDebtInfo = debtQuestion?.allotandDebtOptions || [];
        const oldTotalDebt = debtQuestion?.totalAssets || 0;
        setOldDebtData({ oldDebtInfo, oldTotalDebt, change: historyChange(debtQuestion) });

        updateOnboardingDetails(data);
        calculateTotalInvestment(newInvestmentData);
        calculateTotalDebt(newDebtData);
        setShowLoader(false);
    };

    useEffect(() => {
        const bookedAppointment = localStorage.getItem("bookedAppointment");

        if (bookedAppointment) {
            setAppointmentDetails(JSON.parse(bookedAppointment));
            setThankYouPopupShow(true);
        }
    }, []);

    //get insighsts data
    const fetchInsightsData = async () => {
        try {
            const response = await getDashboardInsights();
            // `null` (AI off, nothing stored), a missing field or an AxiosError
            // all normalise to [] and render the empty state.
            setFinancialInsights(normalizeInsights(response?.data));
        } catch (error) {
            console.error("Error fetching insights data", error);
        }
    };

    //get recent activities
    const fetchTransactionsData = async () => {
        try {
            const response = await getPlaidTransactions();
            if (response) {
                setPlaidActivities(response)
            }
        } catch (error) {
        }
    };

    //get financial goals
    const fetchFinancialGoalsData = async () => {
        try {
            const response = await getFinancialGoals();
            if (Array.isArray(response) && response.length > 0) {
                const goals = response[0]?.goals || [];
                setFinancialGoals(goals);
            }
        } catch (error) {
            console.error("Error fetching financial goals", error);
        }
    };


    useEffect(() => {
        fetchInsightsData();
        fetchTransactionsData();
        fetchFinancialGoalsData();
    }, []);


    // File attach handlers
    const handleAttachClick = () => {
        if (isUploading) return; // Prevent attaching during upload
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        // This popup posts to the same endpoint as the /thread composer, so it
        // has to enforce the same policy: attachmentPolicy.js, which mirrors
        // the backend's utils/attachmentPolicy.js. The inline list it replaces
        // was narrower (no webp, no .txt — both allowed by the spec, the
        // server, mobile and /thread), matched on filename extension rather
        // than MIME type, and repeated the 25MB cap as a bare literal. The
        // popup's own 10-file slot limit is not part of that shared policy and
        // stays here.
        const currentCount = attachedFiles.length;
        const availableSlots = Math.max(0, 10 - currentCount);
        const limitedFiles = files.slice(0, availableSlots);

        const { ok: validFiles, rejected } = validateFiles(limitedFiles);
        rejected.forEach(({ file, reason }) => {
            console.warn(`Attachment rejected (${reason}):`, file.name);
            enqueueSnackbar(`${file.name}: ${reason}`, { variant: 'error' });
        });

        setAttachedFiles((prev) => [...prev, ...validFiles]);

        // reset input so same file can be re-selected later if needed
        e.target.value = null;
    };

    const handleRemoveAttachedFile = (indexToRemove) => {
        if (isUploading) return; // Prevent removal during upload
        setAttachedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };


    const handleSendMessage = async () => {
        if (!messageInput.trim() && attachedFiles.length === 0) {
            enqueueSnackbar('Please enter a message or attach a file', { variant: 'warning' });
            return;
        }

        if (!threadId) {
            enqueueSnackbar('Chat not initialized', { variant: 'error' });
            return;
        }
        setIsUploading(true);
        try {
            const attachments = attachedFiles.length ? await uploadThreadAttachments(attachedFiles) : [];
            await sendThreadMessage(threadId, { text: messageInput.trim(), attachments: attachments.map((a) => ({ documentId: a.documentId })) });
            enqueueSnackbar('Sent. Your advisors will see it in your thread.', { variant: 'success' });
            setMessageInput(''); setAttachedFiles([]); setAskQuestionPopupShow(false);
        } catch (err) {
            console.error("Error sending thread message:", err?.message);
            enqueueSnackbar("Couldn't send. Try again.", { variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const checkBlocked = async () => {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'))?.user;
        if (currentUser?.userType === 'super-admin' || currentUser?.userType === 'advisor' || currentUser?.userType === 'admin') {
            return;
        }
        const userId = currentUser?._id;
        
        try {
            // Call the API to check the user's blocked status
            const response = await checkBlockedStatus(userId);
            if (response.data ? response.data.status === 401 : response.response.status === 401) {
                clearSession();
                navigate("/login");
            } else {
                const { isBlocked, isDeleted } = response.data;

                if (isBlocked || isDeleted) {
                    clearSession();
                    navigate("/login");
                    return;
                }
                // Check if the user does not have a selected plan or has a false payment status
                // if (!currentUser?.selected_plan || currentUser?.payment_status === "false") {
                //     navigate("/package");
                // }
                if (!currentUser?.isPhoneVerified) {
                    navigate("/verify-code");
                }
            }
        } catch (error) {
            console.error("Error checking blocked status:", error);
        }
    };
    const updateOnboardingDetails = (data) => {
        const updatedOnboardingDetails = data?.data?.data?.reduce((acc, item) => {
            acc[item.questionName] = item.selectedOptions || []; // keep old structure
            return acc;
        }, {});

        setOnboardingDetails((prevDetails) => ({
            ...prevDetails,
            ...updatedOnboardingDetails,
        }));

        const updatedOthers = data?.data?.data?.reduce((acc, item) => {
            acc[item.questionName] = item.others || "";
            return acc;
        }, {});

        setOthersData(updatedOthers);

        updateChartData(updatedOnboardingDetails);
    };


    const nonEmptyInvestmentValues = (details) => {
        return details && details.some(item => item?.value?.trim() !== '');
    };

    const calculateTotalInvestment = (details) => {
        const total = (details || []).reduce((total, item) => {
            const value = parseFloat(item?.value) || 0;
            return total + value;
        }, 0);
        return total;
    };

    const investmentPercentageChange = oldInvestmentData?.change ?? null;

    const nonEmptyDebtValues = (debtDetails) => {
        return debtDetails && debtDetails.some(item => item?.value?.trim() !== '');
    };

    const calculateTotalDebt = (debtDetails) => {
        const total = (debtDetails || []).reduce((total, item) => {
            const value = parseFloat(item?.value) || 0;
            return total + value;
        }, 0);
        return total;
    };

    const debtPercentageChange = oldDebtData?.change ?? null;

    // Function to update chart data
    const updateChartData = (currentOnboardingDetails = null) => {
        const node = rootRef.current || undefined;

        // Use current onboarding details or fallback to state
        const details = currentOnboardingDetails || onboardingDetails;

        // Old Investment Data
        const oldInvestmentLabels = oldInvestmentData?.oldInvestmentInfo?.map(item => item.name);
        const oldInvestmentValues = oldInvestmentData?.oldInvestmentInfo?.map(item => parseFloat(item.value) || 0);
        setOldInvestmentChartData({
            labels: oldInvestmentLabels,
            datasets: [{
                data: oldInvestmentValues,
                backgroundColor: ladder(ASSET_STEPS, oldInvestmentValues?.length || 0, node),
            }],
        });

        // Old Debt Data
        const oldDebtLabels = oldDebtData?.oldDebtInfo?.map(item => item.name);
        const oldDebtValues = oldDebtData?.oldDebtInfo?.map(item => parseFloat(item.value) || 0);
        setOldDebtChartData({
            labels: oldDebtLabels,
            datasets: [{
                data: oldDebtValues,
                backgroundColor: ladder(DEBT_STEPS, oldDebtValues?.length || 0, node),
            }],
        });

        // Investment Data
        const investmentData = details["Investments"] || [];
        const investmentLabels = investmentData?.map(item => item?.name);
        const investmentValues = investmentData?.map(item => parseFloat(item?.value) || 0);
        setInvestmentChartData({
            labels: investmentLabels,
            datasets: [{
                data: investmentValues,
                backgroundColor: ladder(ASSET_STEPS, investmentValues?.length || 0, node), // Apply dynamic colors
            }],
        });

        // Debt Data
        const debtData = details["Debts"] || [];
        const debtLabels = debtData.map(item => item.name);
        const debtValues = debtData.map(item => parseFloat(item.value) || 0);
        setDebtChartData({
            labels: debtLabels,
            datasets: [{
                data: debtValues,
                backgroundColor: ladder(DEBT_STEPS, debtValues?.length || 0, node), // Apply dynamic colors
            }],
        });
    };

    // Pie chart options for donut charts
    const pieChartOptions = {
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            }
        },
        layout: {
            padding: {
                top: 10,
                bottom: 10,
                left: 10,
                right: 10,
            },
        },
        elements: {
            arc: { borderColor: readToken("surface", rootRef.current || undefined) },
        },
        responsive: true,
    };

    



    const closePopup = () => {
        setPopupShow(false);
        setCurrentChartType("");
    };

    const handleModalClose = async () => {
       
        setThankYouPopupShow(false);
        window.localStorage.removeItem("bookedAppointment");
        //onClose()
    }

    useEffect(() => {
        checkBlocked();
        getData();

    }, []); //eslint-disable-line

    // Update chart data when old data is loaded
    useEffect(() => {
        if (oldInvestmentData?.oldInvestmentInfo && oldDebtData?.oldDebtInfo) {
            updateChartData();
        }
    }, [oldInvestmentData, oldDebtData]); // eslint-disable-line

    //Added Loader for page load and before unload
    useEffect(() => {
        // Hide loader after page load
        window.addEventListener('load', () => {
            setShowLoader(false);
        });

        // Show loader before page unload
        window.addEventListener('beforeunload', () => {
            setShowLoader(true);
        });

        return () => {
            window.removeEventListener('load', () => { });
            window.removeEventListener('beforeunload', () => { });
        };
    }, []);


    

    const [activeTab, setActiveTab] = useState("Overview");

    const tabs = ["Overview", "Investment & Debts", "Goals", "Insights"];

    


    const hasNetWorthData = hasNetWorthDataFrom(onboardingDetails);
    const netWorthAmount = getNetWorthDisplayValue(
        hasNetWorthData,
        onboardingDetails,
        calculateTotalInvestment,
        calculateTotalDebt
    );
    const hasInvestmentData = nonEmptyInvestmentValues(onboardingDetails?.["Investments"]);
    const totalInvestmentAmount = formatUsd(calculateTotalInvestment(onboardingDetails["Investments"]));
    const hasInvestmentBreakdownData = hasChartBreakdownData(investmentChartData);
    const hasDebtBreakdownData = hasChartBreakdownData(debtChartData);
    const hasDebtData = nonEmptyDebtValues(onboardingDetails?.["Debts"]);
    const totalDebtAmount = formatUsd(calculateTotalDebt(onboardingDetails["Debts"]));


    return (
        <TooltipProvider>
            {renderLoadingState(showLoader,
                <Page>
                  <div ref={rootRef}>
                    {renderDashboardHeader(isClientDashboardUser, params?.clientId ? params?.clientId : null)}
                    {renderWhen(isClientDashboardUser, (
                        <div>
                            <Panel className="tw-mb-6 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                                <div className="tw-flex tw-items-center tw-gap-4">
                                    <StarsIcon name="video" className="tw-h-8 tw-w-8 tw-shrink-0 tw-text-text-accent" />
                                    <span>
                                        <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Connect with your advisor</h2>
                                        <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-text-muted">
                                            Message your advisor anytime or book a video call. They typically respond within one business day.
                                        </p>
                                    </span>
                                </div>

                                <div className="tw-flex tw-flex-wrap tw-gap-3">
                                    <Button variant="quiet" onClick={chatClosed ? () => navigate(CHOOSE_PLAN_PATH) : openAskQuestionPopup}>
                                        Message Your Advisor
                                    </Button>
                                    <Button
                                        variant="quiet"
                                        onClick={() => (chatClosed ? navigate(CHOOSE_PLAN_PATH) : setBookAppointmentPopup(true))}
                                    >
                                        Book a Call
                                    </Button>
                                    {bookAppointmentPopup && <BookAppointmentModal onClose={() => setBookAppointmentPopup(false)} />}
                                </div>
                            </Panel>

                            <div className="tw-mb-12 tw-grid tw-gap-6 max-rail:tw-gap-3 lg:tw-grid-cols-3">
                                <Pass kind="Net Worth" material="sky" compact={isPhone} className="tw-relative tw-overflow-hidden">
                                    <p className="tw-m-0 tw-text-sm tw-text-text">Your total assets minus your total debts.</p>
                                    {subscribed && hasNetWorthData && <Figure>{netWorthAmount}</Figure>}
                                    {subscribed && !hasNetWorthData && <AmountEmpty />}
                                    {!subscribed && <Figure>$0.00</Figure>}
                                    {!subscribed && <NonSubLockBox className="tw-rounded-pass" />}
                                </Pass>

                                <Pass kind="Total Assets" material="aurora" compact={isPhone} className="tw-relative tw-overflow-hidden">
                                    <p className="tw-m-0 tw-text-sm tw-text-text">Everything you own that has financial value.</p>
                                    {subscribed && hasInvestmentData && (
                                        <div className="tw-flex tw-items-end tw-justify-between tw-gap-4">
                                            <Figure>
                                                {totalInvestmentAmount}
                                            </Figure>
                                            <Change value={investmentPercentageChange} />
                                        </div>
                                    )}
                                    {subscribed && !hasInvestmentData && <AmountEmpty />}
                                    {!subscribed && <Figure>$0.00</Figure>}
                                    {!subscribed && <NonSubLockBox className="tw-rounded-pass" />}
                                </Pass>

                                <Pass kind="Total Debt" material="blue" compact={isPhone} className="tw-relative tw-overflow-hidden">
                                    <p className="tw-m-0 tw-text-sm tw-text-text">Everything you owe.</p>
                                    {subscribed && hasDebtData && (
                                        <div className="tw-flex tw-items-end tw-justify-between tw-gap-4">
                                            <Figure>
                                                {totalDebtAmount}
                                            </Figure>
                                            <Change value={debtPercentageChange} />
                                        </div>
                                    )}
                                    {subscribed && !hasDebtData && <AmountEmpty />}
                                    {!subscribed && <Figure>$0.00</Figure>}
                                    {!subscribed && <NonSubLockBox className="tw-rounded-pass" />}
                                </Pass>
                            </div>

                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <TabsList aria-label="Dashboard sections">
                                    {tabs.map((tab) => (
                                        <TabsTrigger key={tab} value={tab}>
                                            {tab}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                <TabsContent value="Overview" className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
                                    <Panel className="tw-relative tw-overflow-hidden">
                                        <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
                                            <h2 className="tw-m-0 tw-text-lg tw-font-bold tw-text-text">Recent Activity</h2>
                                            <Tooltip content="Your latest transactions and account updates.">
                                                <Button variant="icon" size="sm" iconOnly aria-label="About Recent Activity" className="tw-border-0 tw-text-text-muted">
                                                    <StarsIcon name="circleHelp" className="tw-h-4 tw-w-4 tw-shrink-0" />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                        <div data-slot="plaid-activity-list" className="tw-max-h-96 tw-overflow-y-auto">
                                            {subscribed && plaidActivities.length > 0 && (
                                                plaidActivities.map((activity, index) => (
                                                    <div className={ROW_CLASS} data-type={activity.type} key={activity}>
                                                        <div className="tw-min-w-0">
                                                            <h3
                                                                className="tw-m-0 tw-text-sm tw-font-bold tw-text-text"
                                                                title={activity.title}
                                                            >
                                                                {activity?.title?.length > 30
                                                                    ? activity.title.slice(0, 30) + "..."
                                                                    : activity.title}
                                                            </h3>
                                                            <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-text-muted">{activity.description}</p>
                                                        </div>
                                                        <div
                                                            data-type={activity.type}
                                                            className={`tw-shrink-0 tw-text-md tw-font-bold tw-tabular-nums ${amountTone(activity.type)}`}
                                                        >
                                                            {(() => {
                                                                const hasAmount = activity?.amount !== undefined && activity?.amount !== null;
                                                                if (!hasAmount) return "";
                                                                const amountSign = activity.amount < 0 ? "-" : "";
                                                                return `${amountSign}$${Math.abs(activity.amount)}`;
                                                            })()}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                            {subscribed && plaidActivities.length === 0 && (
                                                <div className={PLACEHOLDER_CLASS}>
                                                    <img
                                                        src={RecentActivityPlaceholder}
                                                        alt="No activities available"
                                                    />
                                                </div>
                                            )}
                                            {!subscribed && (
                                                [0, 1, 2].map((n) => (
                                                    <div className={ROW_CLASS} key={n}>
                                                        <div>
                                                            <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-text">Plaid Transaction Title</h3>
                                                            <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-text-muted">Plaid Transaction Description</p>
                                                        </div>
                                                        <div className="tw-text-md tw-font-bold tw-text-text">$0.00</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        {!subscribed && (
                                            <NonSubLockBox className="tw-rounded-lg" />
                                        )}
                                    </Panel>

                                    <Link to={questionsEditPath("q2_goals")} className={LINK_CLASS}>
                                        <Panel title="Financial Goals" className="tw-h-full tw-relative tw-overflow-hidden">
                                            <div data-slot="financial-goals-list" className="tw-max-h-96 tw-overflow-y-auto">
                                                {subscribed && financialGoals.length > 0 && (
                                                    financialGoals.map((goal, index) => (
                                                        <div className={ROW_CLASS} key={goal}>
                                                            <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">{goal}</h3>
                                                        </div>
                                                    ))
                                                )}
                                                {subscribed && financialGoals.length === 0 && (
                                                    <div className={PLACEHOLDER_CLASS}>
                                                        <img
                                                            src={FinancialGoalsPlaceholder}
                                                            alt="No financial goals"
                                                        />
                                                    </div>
                                                )}
                                                {!subscribed && (
                                                    [25, 50, 75].map((pct) => (
                                                        <div className="tw-py-3" key={pct}>
                                                            <div className="tw-flex tw-justify-between tw-gap-4">
                                                                <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">Plaid Financial Goal Title</h3>
                                                                <small className="tw-text-xs tw-text-text-muted">$0.00 / $0.00</small>
                                                            </div>
                                                            <div className="tw-mt-2 tw-h-2 tw-overflow-hidden tw-rounded-pill tw-bg-surface-sunken">
                                                                <div className="tw-h-full tw-bg-action" role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100"
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            {!subscribed && (
                                                <NonSubLockBox className="tw-rounded-lg" />
                                            )}
                                        </Panel>
                                    </Link>

                                    <Link to={questionsEditPath("q1_priorities")} className={LINK_CLASS}>
                                        <Panel title="Top Priorities" className="tw-h-full">
                                            {renderTopPrioritiesContent(onboardingDetails, othersData)}
                                        </Panel>
                                    </Link>
                                </TabsContent>

                                <TabsContent value="Investment & Debts" className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
                                    <Link to={questionsEditPath("q6_investments")} className={LINK_CLASS}>
                                        <Panel title="Assets Allocation" className="tw-h-full tw-relative tw-overflow-hidden">
                                            {renderAssetsBreakdownContent(
                                                subscribed,
                                                hasInvestmentBreakdownData,
                                                onboardingDetails,
                                                investmentChartData,
                                                pieChartOptions,
                                                calculateTotalInvestment
                                            )}
                                            {!subscribed && (
                                                <NonSubLockBox className="tw-rounded-lg" />
                                            )}
                                        </Panel>
                                    </Link>

                                    <Link to={questionsEditPath("q5_debts")} className={LINK_CLASS}>
                                        <Panel title="Debt Breakdown" className="tw-h-full tw-relative tw-overflow-hidden">
                                            {renderDebtBreakdownContent(
                                                subscribed,
                                                hasDebtBreakdownData,
                                                onboardingDetails,
                                                debtChartData,
                                                pieChartOptions,
                                                calculateTotalDebt
                                            )}
                                            {!subscribed && (
                                                <NonSubLockBox className="tw-rounded-lg" />
                                            )}
                                        </Panel>
                                    </Link>
                                </TabsContent>

                                <TabsContent value="Goals" className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
                                    <Link to={questionsEditPath("q3_planning", "shortTerm")} className={LINK_CLASS}>
                                        <Panel title="Short Term Plans" className="tw-h-full">
                                            <div data-slot="selected-short-terms" className="tw-max-h-96 tw-overflow-y-auto">
                                                {renderPlanOptionsContent(onboardingDetails, othersData, "Short-term")}
                                            </div>
                                        </Panel>
                                    </Link>

                                    <Link to={questionsEditPath("q3_planning", "longTerm")} className={LINK_CLASS}>
                                        <Panel title="Long Term Plans" className="tw-h-full">
                                            <div data-slot="selected-long-terms" className="tw-max-h-96 tw-overflow-y-auto">
                                                {renderPlanOptionsContent(onboardingDetails, othersData, "Long-term")}
                                            </div>
                                        </Panel>
                                    </Link>
                                </TabsContent>

                                <TabsContent value="Insights">
                                    <InsightsPanel financialInsights={financialInsights} subscribed={subscribed} />
                                </TabsContent>
                            </Tabs>
                        </div>
                    ))}
                    {shouldShowQuestionnaireDetails && (
                            <StaffAnswers clientId={params.clientId} />
                        )}
                    <DashboardCharts chartType={currentChartType} investmentData={investmentChartData}
                        oldInvestmentData={oldInvestmentChartData}
                        debtData={debtChartData}
                        oldDebtData={oldDebtChartData} popupShow={popupShow} popupClose={closePopup} />
                  </div>
                </Page>)}

            <Dialog open={askQuestionPopupShow} onOpenChange={(open) => !open && askQuestionPopupClose()}>
                <DialogContent
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <DialogTitle>Let’s get in touch</DialogTitle>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" iconOnly aria-label="Close">
                                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            </Button>
                        </DialogClose>
                    </div>
                    <DialogDescription>Want your advisor to take a look at something? You can upload documents here — like pay stubs, tax returns, statements, or anything affecting your money.</DialogDescription>
                    <div className="tw-mt-4">
                        <Textarea
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            placeholder="Send Message"
                            rows="7"
                            maxLength={8000}
                            disabled={isUploading}
                        />
                    </div>

                    {attachedFiles.length > 0 && (
                        <div data-slot="chat-uploaded-document" className="tw-mt-4 tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface-sunken tw-p-3">
                            <div data-slot="chat-uploaded-document-header">
                                <span className="tw-text-sm tw-font-semibold tw-text-text">
                                    {getAttachedFilesText(attachedFiles.length)}
                                </span>
                            </div>
                            <div data-slot="chat-uploaded-document-list">
                                {attachedFiles.map((file, index) => (
                                    <div key={file} data-slot="chat-uploaded-document-item" className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-1">
                                        <div data-slot="chat-uploaded-document-name" className="tw-min-w-0 tw-truncate tw-text-sm tw-text-text" title={file.name}>
                                            {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
                                        </div>
                                        {isUploading ? (
                                            <div data-slot="chat-uploaded-document-progress">
                                                <div data-slot="circular-progress" className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-pill tw-border-2 tw-border-solid tw-border-divider tw-border-t-action motion-reduce:tw-animate-none"></div>
                                            </div>
                                        ) : (
                                            <Button
                                                variant="icon"
                                                size="sm"
                                                iconOnly
                                                data-slot="chat-uploaded-document-remove"
                                                className="tw-border-0 tw-text-text-muted hover:tw-text-text"
                                                onClick={() => handleRemoveAttachedFile(index)}
                                                title="Remove file"
                                                aria-label="Remove file"
                                            >
                                                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <DialogActions className="tw-justify-between">
                        <small className="tw-text-xs tw-text-text-muted"><i>Your advisor typically responds within 1 business day.</i></small>
                        <div className="tw-flex tw-gap-3">
                            <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept={ACCEPT_ATTR}
                                className="tw-hidden"
                                onChange={handleFileChange}
                            />
                            <Button
                                variant="quiet"
                                iconOnly
                                aria-label="Attach files"
                                onClick={handleAttachClick}
                                disabled={isUploading}
                            >
                                <StarsIcon name="paperclip" className="tw-h-5 tw-w-5 tw-shrink-0" />
                            </Button>
                            <Button
                                onClick={handleSendMessage}
                                disabled={isUploading}
                            >
                                {renderAskAdvisorSendButtonContent(isUploading)}
                            </Button>
                        </div>
                    </DialogActions>
                </DialogContent>
            </Dialog>

            {/* thank you popup */}
            <Dialog open={thankYouPopupShow} onOpenChange={(open) => !open && handleModalClose()}>
                <DialogContent
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <div className="tw-flex tw-justify-end">
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" iconOnly aria-label="Close">
                                <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            </Button>
                        </DialogClose>
                    </div>

                    <DialogTitle className="tw-text-center">Thank you! Your meeting is confirmed.</DialogTitle>
                    <DialogDescription className="tw-mb-6 tw-text-center">
                        Email sent! Check your inbox for detailed information!
                    </DialogDescription>
                    <div data-slot="appointment-dtls-box" className="tw-rounded-md tw-bg-surface-sunken tw-p-4">
                        <div className="tw-mb-4">
                            <p className="tw-m-0 tw-mb-1 tw-text-sm tw-text-text-muted">You're meeting with</p>
                            <strong className="tw-text-text">{appointmentDetails?.advisor?.firstName} {appointmentDetails?.advisor?.lastName}</strong>
                        </div>

                        <div className="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <StarsIcon name="calendar" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted" />
                                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">Date & Time</p>
                            </div>
                            <div>
                                <p className="tw-m-0 tw-text-sm tw-text-text">{moment.utc(appointmentDetails?.appointment?.date).format('LL')}, {appointmentDetails?.appointment?.time}</p>
                            </div>
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <StarsIcon name="info" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted" />
                                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">Details</p>
                            </div>
                            <div>
                                <p className="tw-m-0 tw-text-sm tw-text-text">We’ve sent and email with your booking details</p>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </TooltipProvider>
    );
}

export default Dashboard;
